import * as THREE from 'three';
import { CONFIG } from '../config.js';
import { Renderer } from './Renderer.js';
import { CameraController } from './Camera.js';
import { InputManager } from './InputManager.js';
import { CollisionSystem } from './CollisionSystem.js';
import { PhysicsEngine } from './PhysicsEngine.js';
import { Cat } from '../entities/Cat.js';
import { Poop } from '../entities/Poop.js';
import { Terrain } from '../world/Terrain.js';
import { Sky } from '../world/Sky.js';
import { WorldGenerator } from '../world/WorldGenerator.js';
import { HUD } from '../ui/HUD.js';
import { ScoringSystem } from '../systems/ScoringSystem.js';

/**
 * @typedef {Object} LightingPreset
 * @property {number} turbidity
 * @property {number} rayleigh
 * @property {number} mieCoefficient
 * @property {number} mieDirectionalG
 * @property {number} sunElevation
 * @property {number} sunAzimuth
 * @property {number} directionalIntensity
 * @property {number} hemisphereIntensity
 */

/** @type {Readonly<Record<string, LightingPreset>>} */
const LIGHTING_PRESETS = Object.freeze({
    clearNoon: {
        turbidity: 2.0,
        rayleigh: 1.0,
        mieCoefficient: 0.005,
        mieDirectionalG: 0.8,
        sunElevation: 62,
        sunAzimuth: 180,
        directionalIntensity: 0.9,
        hemisphereIntensity: 0.6,
    },
    goldenHour: {
        turbidity: 4.0,
        rayleigh: 0.8,
        mieCoefficient: 0.01,
        mieDirectionalG: 0.75,
        sunElevation: 12,
        sunAzimuth: 210,
        directionalIntensity: 0.7,
        hemisphereIntensity: 0.5,
    },
    overcast: {
        turbidity: 8.0,
        rayleigh: 0.5,
        mieCoefficient: 0.003,
        mieDirectionalG: 0.6,
        sunElevation: 48,
        sunAzimuth: 180,
        directionalIntensity: 0.5,
        hemisphereIntensity: 0.8,
    },
    crispBlue: {
        turbidity: 1.0,
        rayleigh: 1.5,
        mieCoefficient: 0.003,
        mieDirectionalG: 0.85,
        sunElevation: 55,
        sunAzimuth: 180,
        directionalIntensity: 1.0,
        hemisphereIntensity: 0.6,
    },
});

/**
 * @typedef {Object} FlameParticle
 * @property {THREE.Mesh} mesh
 * @property {THREE.Vector3} velocity
 * @property {number} lifeMs
 * @property {'jet' | 'burn' | 'burnVent' | 'smoke'} kind
 * @property {THREE.Vector3=} streamAnchor
 * @property {THREE.Vector3=} streamAxis
 * @property {number=} travelDistance
 * @property {number=} burnHeat
 * @property {number=} burnCooling
 * @property {THREE.Vector3=} ventDirection
 * @property {number=} ventAgeMs
 */

/**
 * Main game orchestrator. Owns the loop, coordinates systems.
 */
export class Game {
    /** @type {THREE.Scene} */
    #scene;

    /** @type {Renderer} */
    #renderer;

    /** @type {CameraController} */
    #camera;

    /** @type {InputManager} */
    #input;

    /** @type {CollisionSystem} */
    #collisions;

    /** @type {PhysicsEngine} */
    #physics;

    /** @type {Cat} */
    #cat;

    /** @type {WorldGenerator} */
    #world;

    /** @type {Terrain} */
    #terrain;

    /** @type {Sky} */
    #sky;

    /** @type {HUD} */
    #hud;

    /** @type {ScoringSystem} */
    #scoring;

    /** @type {THREE.TextureLoader} */
    #textureLoader;

    /** @type {THREE.Texture} */
    #poopTexture;

    /** @type {Poop[]} */
    #poops = [];

    /** @type {boolean} */
    #flamethrowerAttached = false;

    /** @type {number} */
    #flameEmissionRate = 4200;

    /** @type {number} */
    #lastFlameEmitAt = 0;

    /** @type {number} */
    #flameEmissionRemainder = 0;

    /** @type {number} */
    #jetMaxRange = 26;

    /** @type {HTMLButtonElement | null} */
    #flamethrowerButton = null;

    /** @type {THREE.Group | null} */
    #flamethrowerModel = null;

    /** @type {THREE.Vector3 | null} */
    #flamethrowerBasePosition = null;

    /** @type {THREE.Object3D | null} */
    #flamethrowerTip = null;

    /** @type {FlameParticle[]} */
    #flameParticles = [];

    /** @type {THREE.SphereGeometry | null} */
    #flameParticleGeometry = null;

    /** @type {THREE.MeshBasicMaterial | null} */
    #flameParticleMaterial = null;

    /** @type {THREE.SphereGeometry | null} */
    #flameCoreGeometry = null;

    /** @type {THREE.MeshBasicMaterial | null} */
    #flameCoreMaterial = null;

    /** @type {THREE.BufferGeometry | null} */
    #burnParticleGeometry = null;

    /** @type {THREE.MeshBasicMaterial | null} */
    #burnParticleMaterial = null;

    /** @type {number} */
    #maxFlameParticles = 9000;

    /** @type {number} */
    #previousTime = 0;

    /** @type {boolean} */
    #running = false;

    /** @type {boolean} */
    #capsLockRunning = false;

    /** @type {THREE.DirectionalLight} */
    #directionalLight;

    /** @type {THREE.HemisphereLight} */
    #hemisphereLight;

    /** @type {THREE.Mesh} */
    #sunDisc;

    /** @type {{
     * panel: HTMLElement,
     * preset: HTMLSelectElement,
     * turbidity: HTMLInputElement,
     * turbidityValue: HTMLElement,
     * rayleigh: HTMLInputElement,
     * rayleighValue: HTMLElement,
     * mieCoefficient: HTMLInputElement,
     * mieCoefficientValue: HTMLElement,
     * mieDirectionalG: HTMLInputElement,
     * mieDirectionalGValue: HTMLElement,
     * sunElevation: HTMLInputElement,
     * sunElevationValue: HTMLElement,
     * directionalIntensity: HTMLInputElement,
     * directionalIntensityValue: HTMLElement,
     * hemisphereIntensity: HTMLInputElement,
     * hemisphereIntensityValue: HTMLElement,
     * toggle: HTMLButtonElement,
     * } | null} */
    #lightingControls = null;

    constructor() {
        // Initialize scene
        this.#scene = new THREE.Scene();

        // Initialize renderer
        this.#renderer = new Renderer();

        // Initialize camera
        this.#camera = new CameraController(this.#renderer.domElement);

        // Initialize input
        this.#input = new InputManager();

        // Initialize collision system
        this.#collisions = new CollisionSystem();

        // Initialize physics
        this.#physics = new PhysicsEngine();

        // Load textures
        this.#textureLoader = new THREE.TextureLoader();
        this.#poopTexture = this.#textureLoader.load('/poop.png');

        // Setup scene
        this.#setupLighting();
        this.#setupWorld();
        this.#setupCat();
        this.#setupFlamethrowerAttachment();
        this.#setupFlameEffect();

        // Initialize HUD
        this.#hud = new HUD();

        // Initialize scoring
        this.#scoring = new ScoringSystem(this.#hud, this.#world.houses.length);

        // Setup button handlers
        this.#setupButtons();
        this.#setupLightingControls();

        this.#lastFlameEmitAt = performance.now();
        // Hide intro screen on start button
        document.getElementById('startBtn').addEventListener('click', (e) => {
            /** @type {HTMLElement} */ (e.target).blur();
            document.getElementById('intro').style.display = 'none';
        });
    }

    /**
     * Setup lighting
     */
    #setupLighting() {
        // Hemisphere light for natural outdoor ambient lighting
        // Sky color from above, ground color from below
        this.#hemisphereLight = new THREE.HemisphereLight(
            CONFIG.lighting.hemisphereSkyColor,
            CONFIG.lighting.hemisphereGroundColor,
            CONFIG.lighting.hemisphereIntensity
        );
        this.#scene.add(this.#hemisphereLight);

        // Directional light with shadows
        this.#directionalLight = new THREE.DirectionalLight(
            CONFIG.lighting.directionalColor,
            CONFIG.lighting.directionalIntensity
        );
        this.#directionalLight.position.set(5, 10, 7);
        this.#directionalLight.castShadow = true;

        // Light target at world origin so shadows point the right direction
        this.#directionalLight.target.position.set(0, 0, 0);
        this.#scene.add(this.#directionalLight.target);

        // Shadow settings
        this.#directionalLight.shadow.mapSize.width = CONFIG.shadow.mapSize;
        this.#directionalLight.shadow.mapSize.height = CONFIG.shadow.mapSize;
        this.#directionalLight.shadow.camera.near = 0.5;
        this.#directionalLight.shadow.camera.far = 500;
        this.#directionalLight.shadow.camera.left = -100;
        this.#directionalLight.shadow.camera.right = 100;
        this.#directionalLight.shadow.camera.top = 100;
        this.#directionalLight.shadow.camera.bottom = -100;
        this.#directionalLight.shadow.bias = CONFIG.shadow.bias;
        this.#directionalLight.shadow.normalBias = CONFIG.shadow.normalBias;
        this.#directionalLight.shadow.radius = CONFIG.shadow.radius;

        this.#scene.add(this.#directionalLight);
    }

    /**
     * Setup world (terrain, sky, clouds, trees, houses, cows)
     */
    #setupWorld() {
        // Terrain
        this.#terrain = new Terrain();
        this.#scene.add(this.#terrain.mesh);

        // Sky
        this.#sky = new Sky();
        this.#scene.add(this.#sky.mesh);

        // Create sun disc and sync lighting with sky
        this.#createSunDisc();
        this.#syncLightingWithSky();

        // World generator
        this.#world = new WorldGenerator();
        this.#world.generate(this.#scene, this.#poopTexture);

        // Register collidables
        this.#world.trees.forEach(tree => {
            this.#collisions.registerCircle(tree, tree.collisionRadius);
        });

        this.#world.houses.forEach(house => {
            this.#collisions.registerBox(house, house.constructor.WIDTH, house.constructor.DEPTH, house.rotation);
        });

        this.#world.cows.forEach(cow => {
            this.#collisions.registerCircle(cow, cow.collisionRadius);
        });
    }

    /**
     * Create visible sun disc in the sky
     */
    #createSunDisc() {
        const sunGeometry = new THREE.SphereGeometry(20, 16, 16);
        const sunMaterial = new THREE.MeshBasicMaterial({
            color: 0xFFFFAA,
            fog: false
        });
        this.#sunDisc = new THREE.Mesh(sunGeometry, sunMaterial);
        this.#scene.add(this.#sunDisc);
    }

    /**
     * Position directional light and sun disc based on sky sun position
     */
    #syncLightingWithSky() {
        const sunDirection = this.#sky.sunPosition;

        // Position directional light in sun direction (scaled for shadow camera)
        this.#directionalLight.position.copy(sunDirection).multiplyScalar(50);

        // Position sun disc far away in sun direction
        this.#sunDisc.position.copy(sunDirection).multiplyScalar(400);
    }

    /**
     * Setup lighting controls panel with presets and runtime sliders
     */
    #setupLightingControls() {
        const panel = document.getElementById('lightingControls');
        if (!panel) {
            return;
        }

        const get = (id) => document.getElementById(id);

        /** @type {Game['#lightingControls']} */
        const controls = {
            panel,
            preset: /** @type {HTMLSelectElement} */ (get('lightingPreset')),
            turbidity: /** @type {HTMLInputElement} */ (get('turbidityDial')),
            turbidityValue: /** @type {HTMLElement} */ (get('turbidityValue')),
            rayleigh: /** @type {HTMLInputElement} */ (get('rayleighDial')),
            rayleighValue: /** @type {HTMLElement} */ (get('rayleighValue')),
            mieCoefficient: /** @type {HTMLInputElement} */ (get('mieCoefficientDial')),
            mieCoefficientValue: /** @type {HTMLElement} */ (get('mieCoefficientValue')),
            mieDirectionalG: /** @type {HTMLInputElement} */ (get('mieDirectionalGDial')),
            mieDirectionalGValue: /** @type {HTMLElement} */ (get('mieDirectionalGValue')),
            sunElevation: /** @type {HTMLInputElement} */ (get('sunElevationDial')),
            sunElevationValue: /** @type {HTMLElement} */ (get('sunElevationValue')),
            directionalIntensity: /** @type {HTMLInputElement} */ (get('directionalIntensityDial')),
            directionalIntensityValue: /** @type {HTMLElement} */ (get('directionalIntensityValue')),
            hemisphereIntensity: /** @type {HTMLInputElement} */ (get('hemisphereIntensityDial')),
            hemisphereIntensityValue: /** @type {HTMLElement} */ (get('hemisphereIntensityValue')),
            toggle: /** @type {HTMLButtonElement} */ (get('toggleLightingPanel')),
        };

        if (!controls.preset || !controls.toggle || !controls.turbidity || !controls.rayleigh || !controls.mieCoefficient || !controls.mieDirectionalG || !controls.sunElevation || !controls.directionalIntensity || !controls.hemisphereIntensity || !controls.turbidityValue || !controls.rayleighValue || !controls.mieCoefficientValue || !controls.mieDirectionalGValue || !controls.sunElevationValue || !controls.directionalIntensityValue || !controls.hemisphereIntensityValue) {
            return;
        }

        this.#lightingControls = controls;
        this.#hydrateLightingControlsFromScene();

        controls.toggle.addEventListener('click', () => {
            const isCollapsed = controls.panel.classList.toggle('collapsed');
            controls.toggle.textContent = isCollapsed ? 'Lighting +' : 'Lighting -';
        });

        controls.preset.addEventListener('change', () => {
            const preset = LIGHTING_PRESETS[controls.preset.value];
            if (!preset) {
                return;
            }

            this.#setLightingControlValues(preset);
            this.#applyLightingFromControls();
        });

        const handleDialInput = () => this.#applyLightingFromControls();

        controls.turbidity.addEventListener('input', handleDialInput);
        controls.rayleigh.addEventListener('input', handleDialInput);
        controls.mieCoefficient.addEventListener('input', handleDialInput);
        controls.mieDirectionalG.addEventListener('input', handleDialInput);
        controls.sunElevation.addEventListener('input', handleDialInput);
        controls.directionalIntensity.addEventListener('input', handleDialInput);
        controls.hemisphereIntensity.addEventListener('input', handleDialInput);
    }

    /**
     * Populate controls based on current runtime values
     */
    #hydrateLightingControlsFromScene() {
        if (!this.#lightingControls) {
            return;
        }

        const scattering = this.#sky.getScattering();
        const angles = this.#sky.getSunAngles();

        this.#setLightingControlValues({
            turbidity: scattering.turbidity,
            rayleigh: scattering.rayleigh,
            mieCoefficient: scattering.mieCoefficient,
            mieDirectionalG: scattering.mieDirectionalG,
            sunElevation: angles.elevation,
            sunAzimuth: angles.azimuth,
            directionalIntensity: this.#directionalLight.intensity,
            hemisphereIntensity: this.#hemisphereLight.intensity,
        });
    }

    /**
     * Set slider values from a preset/runtime state
     * @param {LightingPreset} values
     */
    #setLightingControlValues(values) {
        if (!this.#lightingControls) {
            return;
        }

        const controls = this.#lightingControls;
        controls.turbidity.value = String(values.turbidity);
        controls.rayleigh.value = String(values.rayleigh);
        controls.mieCoefficient.value = String(values.mieCoefficient);
        controls.mieDirectionalG.value = String(values.mieDirectionalG);
        controls.sunElevation.value = String(values.sunElevation);
        controls.directionalIntensity.value = String(values.directionalIntensity);
        controls.hemisphereIntensity.value = String(values.hemisphereIntensity);

        controls.turbidityValue.textContent = Number(values.turbidity).toFixed(2);
        controls.rayleighValue.textContent = Number(values.rayleigh).toFixed(2);
        controls.mieCoefficientValue.textContent = Number(values.mieCoefficient).toFixed(4);
        controls.mieDirectionalGValue.textContent = Number(values.mieDirectionalG).toFixed(3);
        controls.sunElevationValue.textContent = `${Math.round(values.sunElevation)}°`;
        controls.directionalIntensityValue.textContent = Number(values.directionalIntensity).toFixed(2);
        controls.hemisphereIntensityValue.textContent = Number(values.hemisphereIntensity).toFixed(2);
    }

    /**
     * Apply runtime lighting from current dial values
     */
    #applyLightingFromControls() {
        if (!this.#lightingControls) {
            return;
        }

        const controls = this.#lightingControls;
        const turbidity = Number(controls.turbidity.value);
        const rayleigh = Number(controls.rayleigh.value);
        const mieCoefficient = Number(controls.mieCoefficient.value);
        const mieDirectionalG = Number(controls.mieDirectionalG.value);
        const sunElevation = Number(controls.sunElevation.value);
        const directionalIntensity = Number(controls.directionalIntensity.value);
        const hemisphereIntensity = Number(controls.hemisphereIntensity.value);

        this.#sky.setScattering({
            turbidity,
            rayleigh,
            mieCoefficient,
            mieDirectionalG,
        });

        const { azimuth } = this.#sky.getSunAngles();
        this.#sky.setSunAngles(sunElevation, azimuth);
        this.#syncLightingWithSky();

        this.#directionalLight.intensity = directionalIntensity;
        this.#hemisphereLight.intensity = hemisphereIntensity;

        this.#setLightingControlValues({
            turbidity,
            rayleigh,
            mieCoefficient,
            mieDirectionalG,
            sunElevation,
            sunAzimuth: azimuth,
            directionalIntensity,
            hemisphereIntensity,
        });
    }

    /**
     * Setup cat
     */
    #setupCat() {
        this.#cat = new Cat();
        this.#cat.onAdd(this.#scene);
        this.#collisions.registerCircle(this.#cat, this.#cat.collisionRadius);
    }

    /**
     * Build a visible flamethrower model attached to the cat's back.
     * Tank sits on the spine, short barrel extends forward to chest area.
     */
    #setupFlamethrowerAttachment() {
        const model = new THREE.Group();
        model.position.set(0, 0.75, 0.4);

        // Fuel tank — horizontal cylinder across the cat's width
        const tank = new THREE.Mesh(
            new THREE.CylinderGeometry(0.15, 0.15, 0.7, 12),
            new THREE.MeshStandardMaterial({ color: 0x444444, metalness: 0.6, roughness: 0.4 })
        );
        tank.rotation.z = Math.PI / 2;
        tank.castShadow = true;
        tank.receiveShadow = true;
        model.add(tank);

        // Straps wrapping down from tank
        const strapMaterial = new THREE.MeshStandardMaterial({ color: 0x1f1f1f, roughness: 0.9 });
        const strapA = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.4, 0.5), strapMaterial);
        strapA.position.set(-0.18, -0.12, -0.05);
        model.add(strapA);
        const strapB = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.4, 0.5), strapMaterial);
        strapB.position.set(0.18, -0.12, -0.05);
        model.add(strapB);

        // Pipe connecting tank to barrel
        const connector = new THREE.Mesh(
            new THREE.CylinderGeometry(0.035, 0.035, 0.22, 8),
            new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.8 })
        );
        connector.rotation.x = Math.PI / 2;
        connector.position.set(0, -0.06, -0.12);
        model.add(connector);

        // Short barrel pointing forward (-Z)
        const nozzle = new THREE.Mesh(
            new THREE.CylinderGeometry(0.04, 0.05, 0.5, 10),
            new THREE.MeshStandardMaterial({ color: 0x555555, metalness: 0.6, roughness: 0.4 })
        );
        nozzle.rotation.x = Math.PI / 2;
        nozzle.position.set(0, -0.06, -0.48);
        nozzle.castShadow = true;
        model.add(nozzle);

        // Muzzle ring at barrel end
        const muzzle = new THREE.Mesh(
            new THREE.CylinderGeometry(0.055, 0.055, 0.1, 10),
            new THREE.MeshStandardMaterial({ color: 0x777777, metalness: 0.7, roughness: 0.3 })
        );
        muzzle.rotation.x = Math.PI / 2;
        muzzle.position.set(0, -0.06, -0.75);
        model.add(muzzle);

        // Flame emission point
        const tip = new THREE.Object3D();
        tip.position.set(0, -0.06, -0.95);
        model.add(tip);

        model.visible = false;
        this.#cat.mesh.add(model);

        this.#flamethrowerModel = model;
        this.#flamethrowerTip = tip;
        this.#flamethrowerBasePosition = model.position.clone();
    }

    /**
     * Keep flamethrower mount synced to cat body bob to avoid clipping.
     */
    #syncFlamethrowerMount() {
        if (!this.#flamethrowerModel || !this.#flamethrowerBasePosition) {
            return;
        }

        const bobOffset = this.#cat.bodyBobOffsetY;
        this.#flamethrowerModel.position.set(
            this.#flamethrowerBasePosition.x,
            this.#flamethrowerBasePosition.y + bobOffset,
            this.#flamethrowerBasePosition.z
        );
    }

    /**
     * Setup reusable flame particle assets — separate geometries
     * for the flamethrower jet (small liquid droplets) vs burning
     * object flames (larger irregular shapes).
     */
    #setupFlameEffect() {
        // Tiny sphere for liquid jet droplets
        this.#flameParticleGeometry = new THREE.SphereGeometry(0.04, 6, 6);
        this.#flameParticleMaterial = new THREE.MeshBasicMaterial({
            color: 0xff9e2a,
            transparent: true,
            opacity: 1.0,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
        });

        // White-hot core for jet droplets to add incendiary intensity.
        this.#flameCoreGeometry = new THREE.SphereGeometry(0.02, 5, 5);
        this.#flameCoreMaterial = new THREE.MeshBasicMaterial({
            color: 0xffc166,
            transparent: true,
            opacity: 0.82,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
        });

        // Burning-object fire uses voxel-like cells (Doom fire inspired),
        // intentionally separate from liquid jet droplet geometry.
        this.#burnParticleGeometry = new THREE.BoxGeometry(0.2, 0.2, 0.2);
        this.#burnParticleMaterial = new THREE.MeshBasicMaterial({
            color: 0x2f0f07,
            transparent: true,
            opacity: 0.95,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
        });
    }

    /**
     * Convert Doom-style heat index to a fire color.
     * @param {number} heat
     * @returns {number}
     */
    #getDoomFireColor(heat) {
        const palette = [
            0x070707, 0x1f0707, 0x2f0f07, 0x470f07, 0x571707, 0x671f07,
            0x771f07, 0x8f2707, 0x9f2f07, 0xaf3f07, 0xbf4707, 0xc74707,
            0xdf4f07, 0xdf5707, 0xd75f07, 0xcf6f0f, 0xcf770f, 0xcf8717,
            0xc7971f, 0xbf9f1f, 0xbfaf2f, 0xb7b737, 0xcfcf6f, 0xdfdf9f,
            0xefefc7, 0xffffff,
        ];

        const index = THREE.MathUtils.clamp(Math.floor(heat), 0, palette.length - 1);
        return palette[index];
    }

    /**
     * Snap values to a voxel grid for chunky fire-cell movement.
     * @param {number} value
     * @param {number} cellSize
     * @returns {number}
     */
    #quantizeFireCell(value, cellSize) {
        return Math.round(value / cellSize) * cellSize;
    }

    /**
     * Setup UI buttons
     */
    #setupButtons() {
        const poopButton = document.getElementById('poopBtn');
        const scratchButton = document.getElementById('scratchBtn');
        this.#flamethrowerButton = /** @type {HTMLButtonElement | null} */ (document.getElementById('flamethrowerBtn'));

        if (poopButton) {
            poopButton.addEventListener('click', (e) => {
                /** @type {HTMLElement} */ (e.target).blur();
                this.#handlePrimaryAction();
            });
        }

        if (scratchButton) {
            scratchButton.addEventListener('click', (e) => {
                /** @type {HTMLElement} */ (e.target).blur();
                this.#handleScratch();
            });
        }

        if (this.#flamethrowerButton) {
            this.#flamethrowerButton.addEventListener('click', (e) => {
                /** @type {HTMLElement} */ (e.target).blur();
                this.#toggleFlamethrowerAttachment();
            });
        }

        this.#syncPrimaryActionUI();
    }

    /**
     * Route the primary Q/Poop action based on flamethrower state
     */
    #handlePrimaryAction() {
        if (this.#flamethrowerAttached) {
            this.#handleFlamethrower();
            return;
        }

        this.#handlePoop();
    }

    /**
     * Toggle flamethrower attachment state
     */
    #toggleFlamethrowerAttachment() {
        this.#flamethrowerAttached = !this.#flamethrowerAttached;
        if (this.#flamethrowerModel) {
            this.#flamethrowerModel.visible = this.#flamethrowerAttached;
        }
        this.#syncPrimaryActionUI();
        this.#hud.showStatus(this.#flamethrowerAttached ? 'Flamethrower attached' : 'Flamethrower detached');
    }

    /**
     * Sync button labels for current primary action mode
     */
    #syncPrimaryActionUI() {
        const poopButton = document.getElementById('poopBtn');
        if (poopButton) {
            poopButton.textContent = this.#flamethrowerAttached ? 'Flame' : 'Poop';
        }

        if (this.#flamethrowerButton) {
            this.#flamethrowerButton.textContent = this.#flamethrowerAttached ? 'Detach Flamethrower' : 'Attach Flamethrower';
        }
    }

    /**
     * Fire flamethrower with continuous liquid-jet emission.
     */
    #handleFlamethrower() {
        const now = performance.now();
        const origin = this.#getFlamethrowerTipWorldPosition();
        const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.#cat.mesh.quaternion).normalize();
        const elapsedMs = Math.min(80, Math.max(0, now - this.#lastFlameEmitAt));
        this.#lastFlameEmitAt = now;

        const emissionBudget = (elapsedMs / 1000) * this.#flameEmissionRate + this.#flameEmissionRemainder;
        const emissionCount = Math.floor(emissionBudget);
        this.#flameEmissionRemainder = emissionBudget - emissionCount;

        if (emissionCount > 0) {
            this.#emitFlameParticles(origin, forward, emissionCount);
        }
    }

    /**
     * Get flamethrower muzzle world position, with fallback to cat chest area
     * @returns {THREE.Vector3}
     */
    #getFlamethrowerTipWorldPosition() {
        if (this.#flamethrowerTip) {
            const tipWorld = new THREE.Vector3();
            this.#flamethrowerTip.getWorldPosition(tipWorld);
            return tipWorld;
        }

        const fallback = this.#cat.position.clone();
        fallback.y = CONFIG.cat.groundHeight + 0.6;
        return fallback;
    }

    /**
     * Handle a jet particle hitting world solids.
     * Returns true when the particle should be removed.
     * @param {THREE.Vector3} particlePosition
     * @returns {boolean}
     */
    #handleJetSolidCollision(particlePosition) {
        const cowHitRadiusSq = 0.95 * 0.95;
        for (const cow of this.#world.cows) {
            const relativeY = particlePosition.y - cow.position.y;
            if (relativeY < 0.6 || relativeY > 2.8) {
                continue;
            }

            const dx = cow.position.x - particlePosition.x;
            const dz = cow.position.z - particlePosition.z;
            if ((dx * dx) + (dz * dz) <= cowHitRadiusSq) {
                if (!cow.burned) {
                    if (cow.burn()) {
                        const points = this.#scoring.awardCowRoast();
                        this.#hud.showScorePopup(`🔥 ROAST BEEF! +${points} pts 🔥`, 1700);
                    }
                }
                return true;
            }
        }

        for (const tree of this.#world.trees) {
            const relativeY = particlePosition.y - tree.position.y;
            if (relativeY < 0.2 || relativeY > 9.8) {
                continue;
            }

            const dx = tree.position.x - particlePosition.x;
            const dz = tree.position.z - particlePosition.z;
            const distanceSq = (dx * dx) + (dz * dz);

            const trunkHitRadiusSq = 0.75 * 0.75;
            const trunkHit = relativeY <= 5.2 && distanceSq <= trunkHitRadiusSq;

            const canopyLocalY = relativeY - 4.0;
            const canopyRadius = 3.0 * Math.max(0, 1 - (canopyLocalY / 6.0));
            const canopyHit = canopyLocalY >= 0 && canopyLocalY <= 6.0 && distanceSq <= canopyRadius * canopyRadius;

            if (trunkHit || canopyHit) {
                if (!tree.burned) {
                    if (tree.burn()) {
                        const points = this.#scoring.awardTreeBurn();
                        this.#hud.showScorePopup(`🌲 WHOOPS, THAT TREE'S TOAST! +${points} pts 🌲`, 1700);
                    }
                }
                return true;
            }
        }

        for (const house of this.#world.houses) {
            const relativeY = particlePosition.y - house.position.y;
            if (relativeY < 0 || relativeY > 10.5) {
                continue;
            }

            const dx = house.position.x - particlePosition.x;
            const dz = house.position.z - particlePosition.z;
            const distanceSq = (dx * dx) + (dz * dz);
            if (distanceSq <= house.collisionRadius * house.collisionRadius) {
                if (typeof house.burn === 'function' && !house.burned) {
                    if (house.burn()) {
                        const points = this.#scoring.awardHouseBurn();
                        this.#hud.showScorePopup(`🏠 PROPERTY VALUE ABSOLUTELY NUKED! +${points} pts 🏠`, 1800);
                    }
                }
                return true;
            }
        }

        return false;
    }

    /**
     * Emit a dense stream of small liquid-fire droplets from the nozzle.
     * @param {THREE.Vector3} origin
     * @param {THREE.Vector3} forward
     * @param {number} count
     */
    #emitFlameParticles(origin, forward, count) {
        if (!this.#flameParticleGeometry || !this.#flameParticleMaterial || !this.#flameCoreGeometry || !this.#flameCoreMaterial) {
            return;
        }

        const right = new THREE.Vector3()
            .crossVectors(forward, new THREE.Vector3(0, 1, 0))
            .normalize();

        for (let i = 0; i < count; i++) {
            if (this.#flameParticles.length >= this.#maxFlameParticles) {
                break;
            }

            const particle = new THREE.Mesh(
                this.#flameParticleGeometry,
                this.#flameParticleMaterial.clone()
            );

            // Much thicker stream cross-section
            const lateralSpread = (Math.random() - 0.5) * 0.032;
            const verticalSpread = (Math.random() - 0.5) * 0.016;
            const speed = 0.15 + Math.random() * 0.06;

            const velocity = forward.clone().multiplyScalar(speed);
            velocity.add(right.clone().multiplyScalar(lateralSpread));
            velocity.y += verticalSpread + 0.012;

            particle.position.copy(origin);
            particle.position.add(right.clone().multiplyScalar((Math.random() - 0.5) * 0.032));
            particle.position.y += (Math.random() - 0.5) * 0.016;

            particle.scale.setScalar(0.38 + Math.random() * 0.42);

            const core = new THREE.Mesh(
                this.#flameCoreGeometry,
                this.#flameCoreMaterial.clone()
            );
            core.scale.setScalar(0.78 + Math.random() * 0.3);
            particle.add(core);

            particle.castShadow = false;
            particle.receiveShadow = false;

            this.#scene.add(particle);
            this.#flameParticles.push({
                mesh: particle,
                velocity,
                lifeMs: 2400 + Math.random() * 1700,
                kind: 'jet',
                streamAnchor: origin.clone(),
                streamAxis: forward.clone(),
                travelDistance: 0,
            });
        }
    }

    /**
     * Emit upward flames from objects currently burning.
     * Uses the burn particle geometry (irregular icosahedron) so these
     * look distinctly different from the liquid flamethrower jet.
     */
    #emitBurningObjectFlames() {
        if (!this.#burnParticleGeometry || !this.#burnParticleMaterial) {
            return;
        }

        const burnGeo = this.#burnParticleGeometry;
        const burnMat = this.#burnParticleMaterial;

        /**
         * @param {THREE.Vector3} basePosition
         * @param {number} intensity
         * @param {number} spreadX
         * @param {number} spreadZ
         * @param {number} spreadY
         * @param {number} baseHeat
         */
        const emitRisingFlame = (basePosition, intensity, spreadX, spreadZ, spreadY, baseHeat) => {
            if (this.#flameParticles.length >= this.#maxFlameParticles) {
                return;
            }

            const particle = new THREE.Mesh(burnGeo, burnMat.clone());
            const fireCellSize = 0.18;
            particle.position.copy(basePosition);
            particle.position.x = this.#quantizeFireCell(
                particle.position.x + (Math.random() - 0.5) * spreadX,
                fireCellSize
            );
            particle.position.z = this.#quantizeFireCell(
                particle.position.z + (Math.random() - 0.5) * spreadZ,
                fireCellSize
            );
            particle.position.y = this.#quantizeFireCell(
                particle.position.y + (Math.random() - 0.5) * spreadY,
                fireCellSize
            );

            const sizeVariation = 0.65 + Math.random() * 1.05;
            particle.scale.setScalar(sizeVariation * intensity);

            // Slight random orientation for pixel-cell flicker
            particle.rotation.set(
                Math.random() * Math.PI * 2,
                Math.random() * Math.PI * 2,
                Math.random() * Math.PI * 2
            );

            const isSmoke = Math.random() < 0.08;
            const kind = isSmoke ? 'smoke' : 'burn';

            if (!isSmoke && particle.material instanceof THREE.MeshBasicMaterial) {
                const heat = Math.max(2, baseHeat + (Math.random() - 0.5) * 4);
                particle.material.color.setHex(this.#getDoomFireColor(heat));
            }

            this.#scene.add(particle);
            this.#flameParticles.push({
                mesh: particle,
                velocity: new THREE.Vector3(
                    (Math.random() - 0.5) * 0.09,
                    isSmoke ? (0.12 + Math.random() * 0.12) : (0.14 + Math.random() * 0.18 * intensity),
                    (Math.random() - 0.5) * 0.09
                ),
                lifeMs: isSmoke ? (1300 + Math.random() * 900) : (550 + Math.random() * 550),
                kind,
                burnHeat: isSmoke ? undefined : Math.max(2, baseHeat + (Math.random() - 0.5) * 5),
                burnCooling: isSmoke ? undefined : (0.28 + Math.random() * 0.35),
            });
        };

        /**
         * Emit a vent flame from a window: starts horizontal, then arcs vertical.
         * @param {THREE.Vector3} position
         * @param {THREE.Vector3} outward
         * @param {number} baseHeat
         */
        const emitWindowVentFlame = (position, outward, baseHeat) => {
            if (this.#flameParticles.length >= this.#maxFlameParticles) {
                return;
            }

            const particle = new THREE.Mesh(burnGeo, burnMat.clone());
            const fireCellSize = 0.18;
            const tangent = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), outward).normalize();

            particle.position.copy(position);
            particle.position.addScaledVector(tangent, (Math.random() - 0.5) * 0.5);
            particle.position.y += (Math.random() - 0.5) * 0.3;
            particle.position.x = this.#quantizeFireCell(particle.position.x, fireCellSize);
            particle.position.y = this.#quantizeFireCell(particle.position.y, fireCellSize);
            particle.position.z = this.#quantizeFireCell(particle.position.z, fireCellSize);
            particle.scale.setScalar(0.7 + Math.random() * 0.65);

            if (particle.material instanceof THREE.MeshBasicMaterial) {
                particle.material.color.setHex(this.#getDoomFireColor(baseHeat + Math.random() * 2));
            }

            const horizontalSpeed = 0.2 + Math.random() * 0.14;
            const velocity = outward.clone().multiplyScalar(horizontalSpeed);
            velocity.addScaledVector(tangent, (Math.random() - 0.5) * 0.05);
            velocity.y = 0.01 + Math.random() * 0.015;

            this.#scene.add(particle);
            this.#flameParticles.push({
                mesh: particle,
                velocity,
                lifeMs: 520 + Math.random() * 380,
                kind: 'burnVent',
                burnHeat: Math.max(4, baseHeat + (Math.random() - 0.5) * 4),
                burnCooling: 0.24 + Math.random() * 0.28,
                ventDirection: outward.clone(),
                ventAgeMs: 0,
            });
        };

        this.#world.cows.forEach(cow => {
            if (!cow.burning) {
                return;
            }

            const base = cow.position.clone();
            base.y += 0.8;
            
            for (let i = 0; i < 16; i++) {
                emitRisingFlame(base, 0.58 + Math.random() * 0.4, 1.3, 1.9, 0.7, 24);
            }
            if (Math.random() < 0.75) {
                emitRisingFlame(base, 0.85, 1.3, 1.9, 0.8, 25);
                emitRisingFlame(base, 0.72, 1.3, 1.9, 0.8, 22);
            }
        });

        this.#world.trees.forEach(tree => {
            if (!tree.burning) {
                return;
            }

            const base = tree.position.clone();
            base.y += 3.5;
            
            for (let i = 0; i < 28; i++) {
                emitRisingFlame(base, 0.75 + Math.random() * 0.55, 2.3, 2.3, 1.6, 24);
            }
            if (Math.random() < 0.8) {
                emitRisingFlame(base, 1.05, 2.3, 2.3, 1.7, 26);
                emitRisingFlame(base, 0.9, 2.3, 2.3, 1.7, 22);
            }
        });

        this.#world.houses.forEach(house => {
            if (!house.burning) {
                return;
            }

            const base = house.position.clone();
            base.y += 4.0;

            for (let i = 0; i < 34; i++) {
                emitRisingFlame(base, 0.85 + Math.random() * 0.6, 4.4, 3.8, 2.0, 24);
            }
            if (Math.random() < 0.9) {
                emitRisingFlame(base, 1.2, 4.2, 3.6, 2.2, 26);
                emitRisingFlame(base, 1.0, 4.2, 3.6, 2.2, 22);
            }

            // Window vent flames: blast out sideways first, then arc upward.
            const houseYaw = house.rotation;
            const front = new THREE.Vector3(0, 0, 1).applyAxisAngle(new THREE.Vector3(0, 1, 0), houseYaw);
            const right = new THREE.Vector3(1, 0, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), houseYaw);

            const windowVents = [
                { local: new THREE.Vector3(-2.5, 3.0, 4.05), normal: front.clone() },
                { local: new THREE.Vector3(2.5, 3.0, 4.05), normal: front.clone() },
                { local: new THREE.Vector3(5.05, 3.0, 0), normal: right.clone() },
                { local: new THREE.Vector3(-5.05, 3.0, 0), normal: right.clone().multiplyScalar(-1) },
            ];

            windowVents.forEach((vent) => {
                const worldWindowPos = vent.local.clone()
                    .applyAxisAngle(new THREE.Vector3(0, 1, 0), houseYaw)
                    .add(house.position);
                for (let i = 0; i < 3; i++) {
                    emitWindowVentFlame(worldWindowPos, vent.normal, 25);
                }
                if (Math.random() < 0.9) {
                    emitWindowVentFlame(worldWindowPos, vent.normal, 23);
                }
                if (Math.random() < 0.75) {
                    emitWindowVentFlame(worldWindowPos, vent.normal, 24);
                }
            });
        });
    }

    /**
     * Remove a particle mesh and dispose all mesh materials (including child meshes).
     * @param {THREE.Mesh} mesh
     */
    #removeFlameParticleMesh(mesh) {
        this.#scene.remove(mesh);
        mesh.traverse((node) => {
            if (!(node instanceof THREE.Mesh)) {
                return;
            }

            const material = node.material;
            if (Array.isArray(material)) {
                material.forEach((mat) => {
                    if (mat instanceof THREE.Material) {
                        mat.dispose();
                    }
                });
            } else if (material instanceof THREE.Material) {
                material.dispose();
            }
        });
    }

    /**
     * Update flame particles
     * @param {number} delta
     */
    #updateFlameEffect(delta) {
        if (this.#flameParticles.length === 0) {
            return;
        }

        const deltaMs = delta * 1000;
        this.#flameParticles = this.#flameParticles.filter((particle) => {
            particle.lifeMs -= deltaMs;

            // --- Physics per kind ---
            if (particle.kind === 'jet') {
                if (particle.streamAnchor && particle.streamAxis) {
                    const toParticle = particle.mesh.position.clone().sub(particle.streamAnchor);
                    const along = Math.max(0, toParticle.dot(particle.streamAxis));
                    const centerlinePoint = particle.streamAnchor.clone().add(
                        particle.streamAxis.clone().multiplyScalar(along)
                    );
                    const cohesion = centerlinePoint.sub(particle.mesh.position);
                    particle.velocity.addScaledVector(cohesion, 0.2 * delta * 60);
                }

                const travel = particle.travelDistance ?? 0;
                const travelProgress = Math.min(1, travel / this.#jetMaxRange);
                // Starts fairly flat, then droops harder at long distance.
                const gravity = 0.007 + Math.max(0, travelProgress - 0.35) * 0.055;
                particle.velocity.y -= gravity * delta * 60;
                // Minimal air drag on liquid
                particle.velocity.multiplyScalar(0.9988);
            } else if (particle.kind === 'burn') {
                // Doom-style upward propagation + random side drift based on heat.
                const heat = particle.burnHeat ?? 12;
                const heatNorm = THREE.MathUtils.clamp(heat / 26, 0, 1);
                particle.velocity.x = particle.velocity.x * 0.78 + (Math.random() - 0.5) * (0.015 + (1 - heatNorm) * 0.045);
                particle.velocity.z = particle.velocity.z * 0.78 + (Math.random() - 0.5) * (0.015 + (1 - heatNorm) * 0.045);
                particle.velocity.y = particle.velocity.y * 0.88 + (0.02 + heatNorm * 0.038);

                particle.burnHeat = Math.max(0, heat - (particle.burnCooling ?? 0.45) * delta * 60);
                particle.mesh.rotation.y += (Math.random() - 0.5) * delta * 4;
            } else if (particle.kind === 'burnVent') {
                const heat = particle.burnHeat ?? 14;
                const heatNorm = THREE.MathUtils.clamp(heat / 26, 0, 1);
                particle.ventAgeMs = (particle.ventAgeMs ?? 0) + deltaMs;

                const phase = THREE.MathUtils.clamp((particle.ventAgeMs ?? 0) / 120, 0, 1);
                const ventHorizontal = (particle.ventDirection ?? new THREE.Vector3(0, 0, 1)).clone();
                ventHorizontal.y = 0;
                if (ventHorizontal.lengthSq() > 0) {
                    ventHorizontal.normalize();
                    particle.velocity.addScaledVector(
                        ventHorizontal,
                        (1 - phase) * (0.08 + heatNorm * 0.03) * delta * 60
                    );
                }

                particle.velocity.x *= 0.8;
                particle.velocity.z *= 0.8;
                particle.velocity.y = particle.velocity.y * 0.84 + (0.02 + phase * (0.055 + heatNorm * 0.035));
                particle.burnHeat = Math.max(0, heat - (particle.burnCooling ?? 0.4) * delta * 60);
            } else {
                particle.velocity.x *= 0.98;
                particle.velocity.z *= 0.98;
                particle.velocity.y += 0.015 * delta * 60;
            }

            if (particle.lifeMs <= 0) {
                this.#removeFlameParticleMesh(particle.mesh);
                return false;
            }

            const movement = particle.velocity.clone().multiplyScalar(delta * 60);
            particle.mesh.position.add(movement);

            if (particle.kind === 'burn' || particle.kind === 'burnVent') {
                const fireCellSize = 0.18;
                particle.mesh.position.x = this.#quantizeFireCell(particle.mesh.position.x, fireCellSize);
                particle.mesh.position.y = this.#quantizeFireCell(particle.mesh.position.y, fireCellSize);
                particle.mesh.position.z = this.#quantizeFireCell(particle.mesh.position.z, fireCellSize);
            }

            if (particle.kind === 'jet') {
                particle.travelDistance = (particle.travelDistance ?? 0) + movement.length();
                if (this.#handleJetSolidCollision(particle.mesh.position)) {
                    this.#removeFlameParticleMesh(particle.mesh);
                    return false;
                }
                if ((particle.travelDistance ?? 0) >= this.#jetMaxRange) {
                    this.#removeFlameParticleMesh(particle.mesh);
                    return false;
                }
            }

            // Liquid splashes away on ground contact
            const groundY = 0.02;
            if (particle.kind === 'jet' && particle.mesh.position.y <= groundY) {
                this.#removeFlameParticleMesh(particle.mesh);
                return false;
            }

            // --- Scale per kind ---
            if (particle.kind === 'jet') {
                // Liquid drops stay roughly the same size
                particle.mesh.scale.multiplyScalar(0.997);
            } else if (particle.kind === 'smoke') {
                particle.mesh.scale.multiplyScalar(1.015);
            } else {
                particle.mesh.scale.multiplyScalar(0.985);
            }

            // --- Color progression per kind ---
            if (particle.mesh.material instanceof THREE.MeshBasicMaterial) {
                if (particle.kind === 'jet') {
                    // Hot jet palette: bright amber/orange with brief white-hot peak.
                    const jetLife = Math.max(0, particle.lifeMs / 3200);
                    particle.mesh.material.opacity = 0.28 + Math.min(0.72, jetLife * 0.75);

                    if (jetLife > 0.72) {
                        particle.mesh.material.color.setHex(0xffb84a);
                    } else if (jetLife > 0.5) {
                        particle.mesh.material.color.setHex(0xff9623);
                    } else if (jetLife > 0.3) {
                        particle.mesh.material.color.setHex(0xff6a00);
                    } else if (jetLife > 0.14) {
                        particle.mesh.material.color.setHex(0xd84f00);
                    } else {
                        particle.mesh.material.color.setHex(0x8f2400);
                    }

                    const coreMesh = particle.mesh.children[0];
                    if (coreMesh instanceof THREE.Mesh && coreMesh.material instanceof THREE.MeshBasicMaterial) {
                        coreMesh.material.opacity = 0.24 + Math.min(0.5, jetLife * 0.75) + Math.random() * 0.06;
                        if (jetLife > 0.82) {
                            coreMesh.material.color.setHex(0xffdc97);
                        } else if (jetLife > 0.52) {
                            coreMesh.material.color.setHex(0xffb65c);
                        } else if (jetLife > 0.25) {
                            coreMesh.material.color.setHex(0xff9836);
                        } else {
                            coreMesh.material.color.setHex(0xff7a1e);
                        }
                    }
                } else if (particle.kind === 'smoke') {
                    const smokeLife = Math.max(0, particle.lifeMs / 2000);
                    particle.mesh.material.opacity = Math.min(0.55, smokeLife * 0.8);
                    const smokeGray = 0.2 + (1 - smokeLife) * 0.15;
                    particle.mesh.material.color.setRGB(smokeGray, smokeGray, smokeGray);
                } else {
                    // Burning-object fire uses Doom-like heat palette,
                    // separate from liquid flamethrower color logic.
                    const heat = particle.burnHeat ?? 0;
                    const heatNorm = THREE.MathUtils.clamp(heat / 26, 0, 1);
                    particle.mesh.material.color.setHex(this.#getDoomFireColor(heat));
                    particle.mesh.material.opacity = 0.3 + heatNorm * 0.7;
                }
            }

            return true;
        });
    }

    /**
     * Cleanup flame particles and particle assets
     */
    #clearFlameParticles() {
        this.#flameParticles.forEach((particle) => {
            this.#removeFlameParticleMesh(particle.mesh);
        });
        this.#flameParticles = [];

        if (this.#flameParticleGeometry) {
            this.#flameParticleGeometry.dispose();
            this.#flameParticleGeometry = null;
        }

        if (this.#flameParticleMaterial) {
            this.#flameParticleMaterial.dispose();
            this.#flameParticleMaterial = null;
        }

        if (this.#flameCoreGeometry) {
            this.#flameCoreGeometry.dispose();
            this.#flameCoreGeometry = null;
        }

        if (this.#flameCoreMaterial) {
            this.#flameCoreMaterial.dispose();
            this.#flameCoreMaterial = null;
        }

        if (this.#burnParticleGeometry) {
            this.#burnParticleGeometry.dispose();
            this.#burnParticleGeometry = null;
        }

        if (this.#burnParticleMaterial) {
            this.#burnParticleMaterial.dispose();
            this.#burnParticleMaterial = null;
        }
    }

    /**
     * Handle poop action
     */
    #handlePoop() {
        // Create poop behind cat
        const backDirection = new THREE.Vector3(0, 0, 1);
        backDirection.applyQuaternion(this.#cat.mesh.quaternion);

        const poopPosition = this.#cat.position.clone().add(backDirection.multiplyScalar(0.8));
        poopPosition.y = 0.1;

        // Check if on a doorstep
        const doorstep = this.#findDoorstepUnderCat();
        if (doorstep) {
            poopPosition.y = doorstep.getDoorstepWorldPosition().y + 0.2;

            if (doorstep.poopOnDoorstep(this.#poopTexture)) {
                const points = this.#scoring.awardDoorstepPoop();
                this.#hud.showScorePopup(`💩 DOORSTEP DEMOLISHED! +${points} pts 💩`, 1700);
            } else {
                this.#hud.showStatus('This doorstep is already pooped on!');
            }
        }

        // Create poop entity
        const poop = new Poop({ position: poopPosition });
        poop.onAdd(this.#scene);
        this.#poops.push(poop);
    }

    /**
     * Handle scratch action
     */
    #handleScratch() {
        const closestTree = this.#findClosestTree();
        if (closestTree) {
            if (closestTree.awardScratch()) {
                const points = this.#scoring.awardTreeScratch();
                this.#hud.showScorePopup(`😼 BARK VIOLATION CITED! +${points} pts 😼`, 1700);
                this.#createScratchMarks(closestTree);
            }
        } else {
            this.#hud.showStatus('Nothing to scratch here!');
        }
    }

    /**
     * Find the closest tree within range
     * @returns {import('../entities/Tree.js').Tree|null}
     */
    #findClosestTree() {
        let closestTree = null;
        let minDistance = 5; // Max scratch distance

        for (const tree of this.#world.trees) {
            const distance = this.#cat.position.distanceTo(tree.position);
            if (distance < minDistance) {
                closestTree = tree;
                minDistance = distance;
            }
        }

        return closestTree;
    }

    /**
     * Create scratch marks on a tree
     * @param {import('../entities/Tree.js').Tree} tree
     */
    #createScratchMarks(tree) {
        const scratchGeometry = new THREE.BoxGeometry(0.05, 0.5, 0.05);
        const scratchMaterial = new THREE.MeshStandardMaterial({ color: 0xD2B48C });

        const dirToCat = new THREE.Vector3().subVectors(this.#cat.position, tree.position).normalize();
        const scratchBase = tree.position.clone();
        scratchBase.x += dirToCat.x * 0.7;
        scratchBase.z += dirToCat.z * 0.7;
        scratchBase.y = 1.5;

        for (let i = 0; i < 3; i++) {
            const scratch = new THREE.Mesh(scratchGeometry, scratchMaterial);
            const scratchPos = scratchBase.clone();
            scratchPos.x += (i - 1) * 0.15;
            scratch.position.copy(scratchPos);
            scratch.lookAt(this.#cat.position);
            this.#scene.add(scratch);
        }
    }

    /**
     * Find doorstep under cat
     * @returns {import('../entities/House.js').House|null}
     */
    #findDoorstepUnderCat() {
        for (const house of this.#world.houses) {
            const doorstepPos = house.getDoorstepWorldPosition();
            const dx = doorstepPos.x - this.#cat.position.x;
            const dz = doorstepPos.z - this.#cat.position.z;
            const distSq = dx * dx + dz * dz;

            if (distSq < 4) {
                return house;
            }
        }
        return null;
    }

    /**
     * Resolve cat movement against landed house debris pieces.
     * Uses segment checks so fast movement cannot tunnel through debris.
     * @param {THREE.Vector3} currentPosition
     * @param {THREE.Vector3} proposedPosition
     * @returns {THREE.Vector3}
     */
    #resolveCatAgainstHouseDebris(currentPosition, proposedPosition) {
        const adjusted = proposedPosition.clone();
        const catRadius = this.#cat.collisionRadius;

        for (let pass = 0; pass < 2; pass++) {
            const segX = adjusted.x - currentPosition.x;
            const segZ = adjusted.z - currentPosition.z;
            const segLenSq = segX * segX + segZ * segZ;

            this.#world.houses.forEach((house) => {
                if (typeof house.getDebrisObstacles !== 'function') {
                    return;
                }

                const obstacles = house.getDebrisObstacles();
                obstacles.forEach((obstacle) => {
                    const minDist = catRadius + obstacle.collisionRadius;

                    let closestX = adjusted.x;
                    let closestZ = adjusted.z;
                    if (segLenSq > 0) {
                        const t = THREE.MathUtils.clamp(
                            ((obstacle.position.x - currentPosition.x) * segX + (obstacle.position.z - currentPosition.z) * segZ) / segLenSq,
                            0,
                            1
                        );
                        closestX = currentPosition.x + segX * t;
                        closestZ = currentPosition.z + segZ * t;
                    }

                    const closestDx = closestX - obstacle.position.x;
                    const closestDz = closestZ - obstacle.position.z;
                    const closestDistSq = closestDx * closestDx + closestDz * closestDz;
                    if (closestDistSq >= minDist * minDist) {
                        return;
                    }

                    let pushDx = adjusted.x - obstacle.position.x;
                    let pushDz = adjusted.z - obstacle.position.z;
                    let pushDistSq = pushDx * pushDx + pushDz * pushDz;

                    if (pushDistSq < 0.000001) {
                        pushDx = segX !== 0 || segZ !== 0 ? segX : 1;
                        pushDz = segZ;
                        pushDistSq = pushDx * pushDx + pushDz * pushDz;
                    }

                    const pushDist = Math.sqrt(pushDistSq);
                    const overlap = minDist - pushDist;
                    if (overlap > 0) {
                        adjusted.x += (pushDx / pushDist) * overlap;
                        adjusted.z += (pushDz / pushDist) * overlap;
                    }
                });
            });
        }

        return adjusted;
    }

    /**
     * Start the game loop
     */
    start() {
        this.#running = true;
        this.#previousTime = performance.now();
        this.#lastFlameEmitAt = this.#previousTime;
        this.#flameEmissionRemainder = 0;
        requestAnimationFrame((t) => this.#loop(t));
    }

    /**
     * Main game loop
     * @param {number} currentTime
     */
    #loop(currentTime) {
        if (!this.#running) return;
        requestAnimationFrame((t) => this.#loop(t));

        const delta = (currentTime - this.#previousTime) / 1000;
        this.#previousTime = currentTime;

        if (delta > 0.1) return; // Skip if tab was inactive

        // Handle input
        this.#handleInput(delta);

        // Snapshot input state for next frame's edge detection
        this.#input.update();

        // Update cat physics
        this.#physics.update(this.#cat.position, delta);

        // Update cat animation
        this.#cat.update(delta);

        // Keep flamethrower aligned with cat locomotion bob
        this.#syncFlamethrowerMount();

        // Update world (cows, clouds)
        this.#world.update(delta);

        this.#emitBurningObjectFlames();

        // Update camera
        this.#camera.update(this.#cat.position, this.#cat.rotation, this.#world.houses);

        // Update poop sprites to face camera
        this.#world.houses.forEach(house => {
            if (house.poopSprite) {
                house.poopSprite.lookAt(this.#cat.position);
            }
        });

        this.#updateFlameEffect(delta);

        // Render
        this.#renderer.render(this.#scene, this.#camera.camera);
    }

    /**
     * Handle player input
     * @param {number} delta
     */
    #handleInput(delta) {
        // Sync running mode with CapsLock toggle state
        const capsLockOn = this.#input.isPressed('toggleRun');
        if (capsLockOn !== this.#capsLockRunning) {
            this.#capsLockRunning = capsLockOn;
            this.#hud.showStatus(this.#capsLockRunning ? "Running mode ON" : "Running mode OFF");
        }

        // Camera controls
        if (this.#input.wasJustPressed('resetCamera')) {
            this.#camera.reset();
            this.#hud.showStatus("Camera reset to default");
        }

        if (this.#input.wasJustPressed('aim')) {
            this.#camera.mode = this.#camera.mode === 'default' ? 'aim' : 'default';
            this.#hud.setAimModeVisible(this.#camera.mode === 'aim');
            this.#hud.showStatus(this.#camera.mode === 'aim' ? "Aim mode ON" : "Aim mode OFF");
        }

        // Actions
        if (this.#input.wasJustPressed('toggleFlamethrower')) {
            this.#toggleFlamethrowerAttachment();
        }

        if (this.#flamethrowerAttached) {
            if (this.#input.isPressed('poop')) {
                this.#handleFlamethrower();
            } else {
                this.#lastFlameEmitAt = performance.now();
                this.#flameEmissionRemainder = 0;
            }
        } else if (this.#input.wasJustPressed('poop')) {
            this.#handlePrimaryAction();
        }

        if (this.#input.wasJustPressed('jump')) {
            this.#physics.jump();
        }

        // Movement
        const isMoving = this.#input.isPressed('forward') || this.#input.isPressed('backward');
        const isRunning = this.#capsLockRunning && isMoving;

        this.#cat.setMovementState(isMoving, isRunning);

        // Rotation
        if (this.#input.isPressed('left')) {
            this.#cat.rotation += CONFIG.cat.turnSpeed * delta;
        }
        if (this.#input.isPressed('right')) {
            this.#cat.rotation -= CONFIG.cat.turnSpeed * delta;
        }

        // Forward/backward movement
        if (isMoving) {
            const moveVector = new THREE.Vector3(0, 0, this.#input.isPressed('forward') ? -1 : 1);
            moveVector.applyAxisAngle(new THREE.Vector3(0, 1, 0), this.#cat.rotation);

            const currentSpeed = this.#capsLockRunning ? CONFIG.cat.runSpeed : CONFIG.cat.walkSpeed;
            const newPosition = this.#cat.position.clone().add(moveVector.multiplyScalar(currentSpeed * delta * 60));

            // Enforce boundaries
            newPosition.x = Math.max(-CONFIG.world.boundary, Math.min(CONFIG.world.boundary, newPosition.x));
            newPosition.z = Math.max(-CONFIG.world.boundary, Math.min(CONFIG.world.boundary, newPosition.z));

            // Resolve collisions
            let adjustedPosition = this.#collisions.resolveMovement(this.#cat, newPosition, this.#cat.position);
            adjustedPosition = this.#resolveCatAgainstHouseDebris(this.#cat.position, adjustedPosition);

            this.#cat.position.x = adjustedPosition.x;
            this.#cat.position.z = adjustedPosition.z;

            if (!this.#physics.isJumping) {
                this.#cat.position.y = CONFIG.cat.groundHeight;
            }
        }
    }

    /**
     * Stop the game loop
     */
    stop() {
        this.#running = false;
        this.#clearFlameParticles();
    }
}
